import { useState, useEffect, useCallback, useId } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Alert,
  AlertTitle,
  Skeleton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PlaceIcon from '@mui/icons-material/Place';
import CodeIcon from '@mui/icons-material/Code';
import CloudIcon from '@mui/icons-material/Cloud';
import LanguageIcon from '@mui/icons-material/Language';
import BusinessIcon from '@mui/icons-material/Business';
import SettingsIcon from '@mui/icons-material/Settings';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import BugReportIcon from '@mui/icons-material/BugReport';
import HistoryIcon from '@mui/icons-material/History';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { contextService, Context, ContextUsage, type ContextType } from '@services/contextService';
import { toDisplayError } from '../../utils/apiError';
import { formatDate } from '@utils/format';

/**
 * The types a person may register a tracked place as.
 *
 * REV-494: 'organization' is an aggregation type, not a manually-created
 * place. REV-810: 'deployment' is no longer offered either — it is bounded
 * exactly as a website is (by the origin of its identifier), so offering both
 * asks for a decision that changes nothing and invites a place to be filed
 * under a type nobody can explain. Places already registered as deployments
 * keep working, and keep that type in their own Settings form.
 *
 * The `satisfies` check keeps this subset honest against the shared
 * CONTEXT_TYPE_VALUES (REV-516).
 */
const CREATABLE_CONTEXT_TYPES = ['repository', 'website'] as const satisfies readonly ContextType[];

const createContextSchema = z.object({
  name: z
    .string()
    .min(1, 'trackedPlaces.validation.nameRequired')
    .max(255, 'trackedPlaces.validation.nameTooLong'),
  // See CREATABLE_CONTEXT_TYPES above for what is offered and why.
  // zod 4 dropped `required_error`; this is its replacement, and it keeps the
  // message scoped to a missing value as before.
  context_type: z.enum(CREATABLE_CONTEXT_TYPES, {
    error: (issue) =>
      issue.input === undefined ? 'trackedPlaces.validation.typeRequired' : undefined,
  }),
  canonical_identifier: z
    .string()
    .min(1, 'trackedPlaces.validation.identifierRequired')
    .max(500, 'trackedPlaces.validation.identifierTooLong'),
  description: z.string().optional(),
});

type CreateContextFormData = z.infer<typeof createContextSchema>;

interface ContextsTabProps {
  orgId: string;
  orgSlug: string;
  /**
   * Pending context suggestions for this org. The org page fetches it once for
   * its tab badge and passes it down, so this tab does not ask again.
   */
  suggestionCount?: number;
}

const getContextTypeIcon = (type: string) => {
  switch (type) {
    case 'repository':
      return <CodeIcon sx={{ color: 'white', fontSize: 28 }} />;
    case 'deployment':
      return <CloudIcon sx={{ color: 'white', fontSize: 28 }} />;
    case 'website':
      return <LanguageIcon sx={{ color: 'white', fontSize: 28 }} />;
    case 'organization':
      return <BusinessIcon sx={{ color: 'white', fontSize: 28 }} />;
    default:
      return <PlaceIcon sx={{ color: 'white', fontSize: 28 }} />;
  }
};

const getStatusColor = (status: string): 'success' | 'warning' | 'default' => {
  switch (status) {
    case 'active':
      return 'success';
    case 'paused':
      return 'warning';
    default:
      return 'default';
  }
};

const getTypeColor = (type: string): 'primary' | 'secondary' | 'info' => {
  switch (type) {
    case 'repository':
      return 'primary';
    case 'deployment':
      return 'info';
    case 'website':
      return 'secondary';
    case 'organization':
      return 'secondary';
    default:
      return 'primary';
  }
};

function ContextsTab({ orgId, orgSlug, suggestionCount = 0 }: ContextsTabProps) {
  const { t } = useTranslation();
  const typeLabelId = useId();
  const typeHelpId = useId();

  const [contexts, setContexts] = useState<Context[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  // When create is rejected at the plan limit (HTTP 422 + usage, REV-412), hold
  // the usage so the dialog can show an upgrade prompt instead of a raw error.
  const [createLimitUsage, setCreateLimitUsage] = useState<ContextUsage | null>(null);
  const [usage, setUsage] = useState<ContextUsage | null>(null);
  // REV-611: which card's Actions menu is open. Holds the context too, so each
  // menu item knows what it acts on without closing over the map iteration.
  const [menuState, setMenuState] = useState<{ anchorEl: HTMLElement; context: Context } | null>(
    null
  );

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<CreateContextFormData>({
    resolver: zodResolver(createContextSchema),
    defaultValues: {
      name: '',
      // REV-938: most prospects start with a website, not a repository.
      context_type: 'website',
      canonical_identifier: '',
      description: '',
    },
  });

  // REV-494: the identifier's meaning depends on the chosen type, so its label
  // and hint follow the selection (repository URL vs deployment/website URL).
  const selectedType = watch('context_type');

  const fetchContexts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // REV-611: the grid shows per-place activity, so ask for the counts.
      const result = await contextService.list(orgId, { include_summary: true });
      setContexts(result.items ?? []);
    } catch (err) {
      setError(t('trackedPlaces.errors.fetchFailed'));
      console.error('Failed to fetch contexts:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId, t]);

  const fetchUsage = useCallback(async () => {
    try {
      const result = await contextService.getUsage(orgId);
      setUsage(result);
    } catch (err) {
      console.error('Failed to fetch usage:', err);
    }
  }, [orgId]);

  useEffect(() => {
    void fetchContexts();
    void fetchUsage();
  }, [fetchContexts, fetchUsage]);

  const handleCreateContext = async (data: CreateContextFormData) => {
    setCreating(true);
    setCreateError(null);
    setCreateLimitUsage(null);
    try {
      await contextService.create(orgId, {
        name: data.name,
        context_type: data.context_type,
        canonical_identifier: data.canonical_identifier,
        description: data.description || undefined,
      });
      setCreateDialogOpen(false);
      reset();
      await Promise.all([fetchContexts(), fetchUsage()]);
    } catch (err) {
      const error = err as {
        response?: { data?: { error?: string; usage?: ContextUsage } };
      };
      const limitUsage = error.response?.data?.usage;
      if (limitUsage) {
        // Plan limit reached (REV-412): show an actionable upgrade prompt.
        setCreateLimitUsage(limitUsage);
        setCreateError(
          t('trackedPlaces.limits.atLimitDetail', {
            used: limitUsage.used,
            limit: limitUsage.limit,
            plan: limitUsage.plan,
          })
        );
      } else {
        setCreateError(toDisplayError(error, t('trackedPlaces.errors.createFailed')));
      }
    } finally {
      setCreating(false);
    }
  };

  const handleOpenCreateDialog = () => {
    reset();
    setCreateError(null);
    setCreateLimitUsage(null);
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setCreateLimitUsage(null);
    reset();
  };

  // REV-732: a destination, not a handler. All three "Upgrade" buttons go to
  // the same URL, so they are links — which also gives them cmd-click and
  // "open in new tab", useful when you are mid-task and do not want to leave.
  const BILLING_HREF = '/settings/billing';

  /**
   * REV-732 slice 6: every item in the Actions menu only navigates, so all
   * three are destinations rather than handlers. They were named handlers
   * calling navigate(), which is why convertedLinks.guard.test.ts listed this
   * file as clean while all three were still buttons — that guard reads inline
   * `onClick={() => navigate(…)}` and cannot follow an identifier.
   *
   * REV-497: link by slug so the URL is readable and recognisable in history.
   * Falls back to the id — the detail route resolves either, so a row from an
   * API that predates the slug column still opens rather than 404ing.
   */
  const manageHref = (context: Context) =>
    `/orgs/${orgSlug}/contexts/${context.slug || context.id}`;

  // Pre-selects the tracked place on the test form.
  const runTestHref = (context: Context) =>
    `/tests/new?contextId=${encodeURIComponent(context.id)}`;

  const findingsHref = (context: Context) =>
    `/orgs/${orgSlug}/findings?contextId=${encodeURIComponent(context.id)}`;

  /**
   * Kept on every converted item, and not only out of caution — see the
   * OrgSwitcher note in slice 5, where omitting it left aria-hidden="true" on
   * #root and removed the destination page from the accessibility tree.
   *
   * Focus restoration is not the concern it would be for a non-navigating item:
   * MUI returns focus to the trigger on close, and the trigger is then
   * unmounted with the rest of the page. (Carried over from runMenuAction's
   * doc comment, which the href conversion removed.)
   *
   * The read-before-close hazard that comment also recorded is gone by
   * construction: each `to` is resolved at render from menuState, so clearing
   * menuState on click cannot race the destination.
   */
  const closeMenu = () => setMenuState(null);

  /**
   * The activity line for a card (REV-611).
   *
   * A never-scanned place must not read as "0 scans, 0 findings" — that looks
   * like a clean bill of health for something nobody has ever looked at, which
   * is the opposite of the truth.
   */
  const renderSummary = (context: Context) => {
    const summary = context.summary;
    if (!summary) {
      // The list resolved but carried no counts: an older API, or the stats
      // aggregate failed. Say so rather than implying zero.
      return (
        <Typography variant="caption" color="text.secondary">
          {t('trackedPlaces.summary.unavailable')}
        </Typography>
      );
    }
    if (summary.totalScans === 0) {
      return (
        <Typography variant="caption" color="text.secondary">
          {t('trackedPlaces.summary.neverScanned')}
        </Typography>
      );
    }
    return (
      <Typography variant="caption" color="text.secondary">
        {t('trackedPlaces.summary.scans', { count: summary.totalScans })}
        {' · '}
        {t('trackedPlaces.summary.openFindings', { count: summary.openFindings })}
        {summary.lastScanAt
          ? ` · ${t('trackedPlaces.summary.lastScanned', {
              date: formatDate(summary.lastScanAt),
            })}`
          : ''}
      </Typography>
    );
  };

  const isAtLimit = usage !== null && usage.limit !== null && usage.used >= usage.limit;
  const isNearLimit = usage !== null && usage.limit !== null && usage.used >= usage.limit * 0.8;

  if (loading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Skeleton variant="text" width={150} height={32} />
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Skeleton variant="text" width={150} height={24} />
            <Skeleton variant="rectangular" width={150} height={36} />
          </Box>
        </Box>
        <Grid container spacing={3}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="circular" width={48} height={48} sx={{ mb: 2 }} />
                  <Skeleton variant="text" width="60%" height={32} />
                  <Skeleton variant="text" width="80%" />
                  <Skeleton variant="text" width="40%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">{t('trackedPlaces.title')}</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Usage Indicator */}
          {usage && (
            <Box sx={{ minWidth: 200 }}>
              <Typography variant="body2" color="text.secondary">
                {usage.limit === null
                  ? t('trackedPlaces.limits.unlimited')
                  : t('trackedPlaces.limits.used', { used: usage.used, limit: usage.limit })}
              </Typography>
              {usage.limit !== null && (
                <LinearProgress
                  variant="determinate"
                  value={(usage.used / usage.limit) * 100}
                  color={isAtLimit ? 'error' : isNearLimit ? 'warning' : 'primary'}
                  sx={{ mt: 0.5 }}
                  // REV-665: without a name this announced only as an unnamed
                  // progressbar. The neighbouring text is not enough — a
                  // progressbar is its own node in the accessibility tree, and
                  // nothing associates the two. The translation already
                  // existed; it had simply never been wired to the element.
                  aria-label={t('trackedPlaces.limits.progressLabel', {
                    used: usage.used,
                    limit: usage.limit,
                    percentage: Math.round((usage.used / usage.limit) * 100),
                  })}
                />
              )}
            </Box>
          )}
          <Button
            variant="text"
            startIcon={<SwapHorizIcon />}
            component={RouterLink}
            to={`/orgs/${orgSlug}/migrate`}
          >
            {t('migration.menuLabel')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            component={RouterLink}
            to={`/orgs/${orgSlug}/contexts/bulk`}
          >
            {t('trackedPlaces.bulkRegister')}
          </Button>
          {/* Stays enabled at the limit (Form UX rule): the user can open the
              dialog and attempt, then sees a clear upgrade prompt on the 422. */}
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateDialog}>
            {t('trackedPlaces.createNew')}
          </Button>
        </Box>
      </Box>

      {/* Plan limit prompts (REV-412/REV-318). At and near the limit we explain
          the situation and offer an upgrade path; scanning stays free regardless. */}
      {usage && isAtLimit && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" component={RouterLink} to={BILLING_HREF}>
              {t('trackedPlaces.limits.upgrade')}
            </Button>
          }
        >
          <AlertTitle>{t('trackedPlaces.limits.atLimit')}</AlertTitle>
          {t('trackedPlaces.limits.atLimitDetail', {
            used: usage.used,
            limit: usage.limit,
            plan: usage.plan,
          })}{' '}
          {t('trackedPlaces.limits.executionsUnlimited')}
        </Alert>
      )}
      {usage && isNearLimit && !isAtLimit && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" component={RouterLink} to={BILLING_HREF}>
              {t('trackedPlaces.limits.upgrade')}
            </Button>
          }
        >
          <AlertTitle>{t('trackedPlaces.limits.nearLimit')}</AlertTitle>
          {t('trackedPlaces.limits.nearLimitDetail', {
            used: usage.used,
            limit: usage.limit,
            plan: usage.plan,
          })}
        </Alert>
      )}

      {/* Suggestions Alert */}
      {suggestionCount > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {suggestionCount === 1
            ? t('trackedPlaces.suggestions.pendingCount', { count: suggestionCount })
            : t('trackedPlaces.suggestions.pendingCountPlural', { count: suggestionCount })}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {contexts.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <PlaceIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t('trackedPlaces.empty.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('trackedPlaces.empty.description')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateDialog}
              disabled={isAtLimit}
            >
              {t('trackedPlaces.createFirst')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {contexts.map((context) => (
            <Grid item xs={12} sm={6} md={4} key={context.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                        bgcolor: getTypeColor(context.context_type) + '.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                      }}
                    >
                      {getContextTypeIcon(context.context_type)}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" noWrap>
                        {context.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {context.canonical_identifier}
                      </Typography>
                    </Box>
                  </Box>
                  {context.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        mb: 2,
                      }}
                    >
                      {context.description}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={t(`trackedPlaces.status.${context.status}`)}
                      size="small"
                      color={getStatusColor(context.status)}
                    />
                    <Chip
                      label={t(`trackedPlaces.types.${context.context_type}`)}
                      size="small"
                      variant="outlined"
                      color={getTypeColor(context.context_type)}
                    />
                  </Box>
                  {/* REV-611: per-place activity, so the grid answers "which of
                      these is being tested, and which is in trouble" without
                      opening each card. Text, not colour alone. */}
                  <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <HistoryIcon fontSize="inherit" color="disabled" aria-hidden="true" />
                    {renderSummary(context)}
                  </Box>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2, justifyContent: 'flex-end' }}>
                  {/* REV-611: one Manage button became a menu. The accessible
                      name carries the place's NAME — thirty identical "Actions"
                      buttons is a screen-reader dead end. 44x44 minimum touch
                      target per WCAG 2.2 AA. */}
                  <IconButton
                    size="small"
                    aria-label={t('trackedPlaces.actions.menuLabel', { name: context.name })}
                    aria-haspopup="true"
                    // Always present, true OR false. Omitting it while closed
                    // leaves a menu button that never announces its state, and
                    // "not aria-expanded=true" is satisfied by an absent
                    // attribute — which is how a unit test can miss this and an
                    // engine audit cannot.
                    aria-expanded={menuState?.context.id === context.id}
                    // Points at the element that actually carries role="menu".
                    // MUI puts the Menu's own id on the Popover ROOT div, so
                    // referencing that would name a plain div as this button's
                    // menu; the id is set on the list via slotProps below.
                    aria-controls={
                      menuState?.context.id === context.id ? 'context-actions-menu' : undefined
                    }
                    onClick={(event) => setMenuState({ anchorEl: event.currentTarget, context })}
                    sx={{ minWidth: 44, minHeight: 44 }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* REV-611: ONE menu for the whole grid, anchored to whichever trigger was
          clicked. A Menu per card would put N popovers in the tree and N sets of
          listeners for a control only ever open once.

          MUI's Menu supplies the roving focus, arrow-key navigation and
          Escape-restores-focus-to-trigger behaviour the ticket requires, so
          none of that is reimplemented here. */}
      <Menu
        anchorEl={menuState?.anchorEl ?? null}
        open={Boolean(menuState)}
        onClose={closeMenu}
        slotProps={{
          list: {
            // The id lives HERE, not on <Menu>: this is the element with
            // role="menu", and it is what the trigger's aria-controls must name.
            id: 'context-actions-menu',
            'aria-label': t('trackedPlaces.actions.menuLabel', {
              name: menuState?.context.name ?? '',
            }),
          },
        }}
      >
        <MenuItem
          component={RouterLink}
          to={menuState ? manageHref(menuState.context) : ''}
          onClick={closeMenu}
          sx={{ minHeight: 44 }}
        >
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('trackedPlaces.actions.manage')}</ListItemText>
        </MenuItem>
        <MenuItem
          component={RouterLink}
          to={menuState ? runTestHref(menuState.context) : ''}
          onClick={closeMenu}
          sx={{ minHeight: 44 }}
        >
          <ListItemIcon>
            <PlayArrowIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('trackedPlaces.actions.runTest')}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          component={RouterLink}
          to={menuState ? findingsHref(menuState.context) : ''}
          onClick={closeMenu}
          sx={{ minHeight: 44 }}
        >
          <ListItemIcon>
            <BugReportIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('trackedPlaces.actions.viewFindings')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create Context Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(handleCreateContext)}>
          <DialogTitle>{t('trackedPlaces.create.title')}</DialogTitle>
          <DialogContent>
            {createError && (
              <Alert
                severity={createLimitUsage ? 'warning' : 'error'}
                sx={{ mb: 2 }}
                action={
                  createLimitUsage ? (
                    <Button color="inherit" size="small" component={RouterLink} to={BILLING_HREF}>
                      {t('trackedPlaces.limits.upgrade')}
                    </Button>
                  ) : undefined
                }
              >
                {createError}
              </Alert>
            )}
            <TextField
              {...register('name')}
              label={t('trackedPlaces.fields.name')}
              fullWidth
              margin="normal"
              error={!!errors.name}
              helperText={errors.name ? t(errors.name.message as string) : undefined}
              disabled={creating}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <Controller
              name="context_type"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth margin="normal" error={!!errors.context_type}>
                  <InputLabel shrink id={typeLabelId}>
                    {t('trackedPlaces.fields.type')}
                  </InputLabel>
                  <Select
                    {...field}
                    // REV-938: without labelId, aria-labelledby names only the
                    // value element, so the "Type" label is never announced.
                    labelId={typeLabelId}
                    // FormControl does not wire this up for a Select.
                    aria-describedby={typeHelpId}
                    label={t('trackedPlaces.fields.type')}
                    disabled={creating}
                    notched
                  >
                    {/* Built from the vocabulary the schema validates, so the
                        two cannot drift (REV-516). */}
                    {CREATABLE_CONTEXT_TYPES.map((ct) => (
                      <MenuItem key={ct} value={ct}>
                        {t(`trackedPlaces.types.${ct}`)}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.context_type ? (
                    <Typography id={typeHelpId} variant="caption" color="error">
                      {t(errors.context_type.message as string)}
                    </Typography>
                  ) : (
                    // REV-810: the type decides what evidence the new place
                    // will accept — by origin for a website or deployment, by
                    // file and component for a repository — so say what the
                    // chosen one means while it is being chosen.
                    <FormHelperText id={typeHelpId}>
                      {t(`trackedPlaces.fields.typeHelpByType.${selectedType}`)}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
            <TextField
              {...register('canonical_identifier')}
              label={t(`trackedPlaces.fields.identifierLabelByType.${selectedType}`, {
                defaultValue: t('trackedPlaces.fields.identifier'),
              })}
              fullWidth
              margin="normal"
              error={!!errors.canonical_identifier}
              helperText={
                errors.canonical_identifier
                  ? t(errors.canonical_identifier.message as string)
                  : t(`trackedPlaces.fields.identifierHintByType.${selectedType}`, {
                      defaultValue: t('trackedPlaces.fields.identifierHint'),
                    })
              }
              disabled={creating}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              {...register('description')}
              label={t('trackedPlaces.fields.description')}
              fullWidth
              margin="normal"
              multiline
              rows={3}
              error={!!errors.description}
              helperText={errors.description ? t(errors.description.message as string) : undefined}
              disabled={creating}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseCreateDialog} disabled={creating}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="contained" disabled={creating}>
              {creating ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('trackedPlaces.create.submit')
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default ContextsTab;
